import {
  ActionIcon,
  Autocomplete,
  Checkbox,
  Fieldset,
  Flex,
  Grid,
  Group,
  Menu,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { IconCopy, IconCopyPlus, IconDots, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useBlocker, useNavigate } from '@tanstack/react-router'
import Form from 'common/Form'
import PhoneNumberInput from 'common/inputs/PhoneNumberInput'
import { optionsFilter } from 'common/inputs/utils'
import { deliveryCompaniesQuery, useAddOrder, useUpdateOrder } from 'hooks/orders'
import { zodResolver } from 'mantine-form-zod-resolver'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useEffect, useMemo, useState } from 'react'
import { DocumentType, NewOrderItem, Order, PaymentMethod } from 'types/orders'
import { getOrderSchema } from 'types/schemas'
import { countries, documentTypes, getCategoryName, getUnitName, isGuid, paymentMethods, trimFormValues } from 'utils'
import z from 'zod'
import { getTrackingUrl } from './utils'

type NewOrder = z.infer<ReturnType<typeof getOrderSchema>>

export default function OrderForm({ order, preview }: { order?: Order; preview?: boolean }) {
  const navigate = useNavigate()
  const { data: deliveryCompanies } = useSuspenseQuery(deliveryCompaniesQuery)
  const addOrder = useAddOrder()
  const updateOrder = useUpdateOrder()
  const [orderItems, setOrderItems] = useState<Array<NewOrderItem>>(order ? order.orderItems : [])
  const [isNameSameAsBilling, setIsNameSameAsBilling] = useState(false)

  function openAddOrderItemModal() {
    const id = modals.openContextModal({
      modal: 'orderItem',
      title: 'Dodaj produkt do zamówienia',
      centered: true,
      innerProps: {
        onSubmit: (newOrderItem: NewOrderItem) => {
          setOrderItems((current) => [...current, { ...newOrderItem, id: current.length.toString() }])
          modals.close(id)
        },
      },
    })
  }

  function openUpdateOrderItemModal(initialValues: NewOrderItem) {
    const id = modals.openContextModal({
      modal: 'orderItem',
      title: 'Edytuj produkt w zamówieniu',
      centered: true,
      innerProps: {
        initialValues,
        onSubmit: (updatedOrderItem: NewOrderItem) => {
          setOrderItems((current) =>
            current.map((item) => (item.id === initialValues.id ? { ...updatedOrderItem, id: initialValues.id } : item))
          )
          modals.close(id)
        },
      },
    })
  }

  function openDeleteOrderItemModal(orderItem: NewOrderItem) {
    modals.openConfirmModal({
      title: 'Usuń produkt z zamówienia',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć produkt <b>{orderItem.productSymbol}</b> z zamówienia? Nie będzie można cofnąć tej
          operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń produkt', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: () => setOrderItems((current) => current.filter((item) => item.id !== orderItem.id)),
    })
  }

  // this state needs to be duplicated here because it needs to be used in the useForm below
  const [isSchemaWithParcelLocker, setIsSchemaWithParcelLocker] = useState(false)
  const [dispatched, setDispatched] = useState(!!order?.dispatchedAt)

  useEffect(() => {
    setDispatched(!!order?.dispatchedAt)
  }, [order?.dispatchedAt])

  const form = useForm<NewOrder>({
    validate: zodResolver(getOrderSchema(isSchemaWithParcelLocker)),
    transformValues: (v) =>
      trimFormValues(v, (values) => ({
        ...values,
        nip: values.nip ? values.nip.toString() : null,
        comments: values.comments || null,
        delivery: {
          ...values.delivery,
          parcelLocker: values.delivery.parcelLocker || null,
        } as NewOrder['delivery'],
      })),
    initialValues: order
      ? {
          firstName: order.firstName,
          lastName: order.lastName,
          email: order.email,
          phoneNumber: order.phoneNumber,
          nip: order.nip || null,
          documentType: order.documentType,
          paymentMethod: order.paymentMethod,
          discountPercent: order.discountPercent || undefined,
          comments: order.comments || '',
          countryCode: order.countryCode,
          delivery: {
            name: order.deliveryAddress.name,
            firstName: order.deliveryAddress.firstName,
            lastName: order.deliveryAddress.lastName,
            street: order.deliveryAddress.street || '',
            buildingNumber: order.deliveryAddress.buildingNumber || '',
            apartmentNumber: order.deliveryAddress.apartmentNumber || '',
            city: order.deliveryAddress.city || '',
            postalCode: order.deliveryAddress.postalCode || '',
            country: order.deliveryAddress.country || '',
            deliveryCompanyId: order.shipmentType.deliveryCompanyId,
            shipmentTypeId: order.shipmentType.id,
            parcelLocker: order.deliveryAddress.parcelLocker || null,
            shippingCost: order.shippingCost,
            parcelSize: order.deliveryAddress.parcelSize || null,
          },
        }
      : ({
          countryCode: 48,
          documentType: DocumentType.RECEIPT,
          paymentMethod: PaymentMethod.PREPAID,
          nip: null,
          delivery: {
            firstName: null,
            lastName: null,
            street: null,
            buildingNumber: null,
            apartmentNumber: null,
            city: null,
            postalCode: null,
            country: 'Polska',
            deliveryCompanyId: null,
            shipmentTypeId: null,
            shippingCost: null,
          },
        } as unknown as NewOrder),
  })

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (preview) return false
      if (next.search.orderCreated === 'true' || next.search.orderUpdated === 'true') return false
      const dirty = form.getDirty()
      const dirtyFields = Object.keys(dirty) as Array<keyof NewOrder>
      if (dirtyFields.length === 4) {
        const allowedFields = [
          'delivery.parcelLocker',
          'delivery.parcelSize',
          'delivery.shipmentTypeId',
          'delivery.shippingCost',
        ]
        if (dirtyFields.every((field) => allowedFields.includes(field))) {
          return false
        }
      }
      return form.isDirty()
    },
    withResolver: true,
    enableBeforeUnload: false,
  })

  const openModal = () => {
    modals.openConfirmModal({
      title: 'Masz niezapisane zmiany',
      children: <Text size="sm">Masz niezapisane zmiany w formularzu. Czy na pewno wyjść z tej strony?</Text>,
      labels: { confirm: 'Tak', cancel: 'Wróć' },
      centered: true,
      withCloseButton: false,
      closeOnClickOutside: false,
      onCancel: reset,
      onConfirm: proceed,
    })
  }

  useEffect(() => {
    if (status === 'blocked') {
      openModal()
    }
  }, [status])

  const shipmentTypes = useMemo(() => {
    const company = deliveryCompanies.find((c) => c.id === form.getValues().delivery?.deliveryCompanyId)
    if (!company) return []
    return company.shipmentTypes.filter((type) => type.isActive).map((type) => ({ label: type.name, value: type.id }))
  }, [deliveryCompanies, form.getValues().delivery?.deliveryCompanyId])

  const isParcelLocker = useMemo(() => {
    const company = deliveryCompanies.find((c) => c.id === form.getValues().delivery?.deliveryCompanyId)
    if (!company) return false
    const shipmentType = company.shipmentTypes.find((type) => type.id === form.getValues().delivery?.shipmentTypeId)
    if (!shipmentType) return false
    return shipmentType.isParcelLocker
  }, [deliveryCompanies, form.getValues().delivery?.deliveryCompanyId, form.getValues().delivery?.shipmentTypeId])

  useEffect(() => {
    setIsSchemaWithParcelLocker(isParcelLocker)
    if (isParcelLocker) {
      form.setFieldValue('delivery.street', null)
      form.setFieldValue('delivery.buildingNumber', null)
      form.setFieldValue('delivery.apartmentNumber', null)
      form.setFieldValue('delivery.city', null)
      form.setFieldValue('delivery.postalCode', null)
      form.setFieldValue('delivery.name', null)
    }
  }, [isParcelLocker])

  const totalItemsPrice = useMemo(() => {
    const total = orderItems.reduce((acc, item) => acc + (item.price ?? 0) * (item.amount ?? 0), 0)
    return total.toFixed(2)
  }, [orderItems])

  const vatAmount = useMemo(() => {
    const vat = (parseFloat(totalItemsPrice) * 100) / 123
    return (parseFloat(totalItemsPrice) - vat).toFixed(2)
  }, [totalItemsPrice])

  const discountValue = useMemo(() => {
    const discountPercent = form.getValues().discountPercent
    if (!discountPercent) return '0'
    return ((parseFloat(totalItemsPrice) * discountPercent) / 100).toFixed(2)
  }, [form.getValues().discountPercent, totalItemsPrice])

  const totalOrderPrice = useMemo(() => {
    return (
      parseFloat(totalItemsPrice) -
      parseFloat(discountValue) +
      (form.getValues().delivery?.shippingCost || 0)
    ).toFixed(2)
  }, [form.getValues().delivery?.shippingCost, totalItemsPrice, discountValue])

  const discountPercent = useMemo(() => {
    return form.getValues().discountPercent
  }, [form.getValues().discountPercent])

  useEffect(() => {
    if (isNameSameAsBilling) {
      form.setFieldValue('delivery.firstName', form.getValues().firstName)
      form.setFieldValue('delivery.lastName', form.getValues().lastName)
    }
  }, [isNameSameAsBilling, form.getValues().firstName, form.getValues().lastName])

  useEffect(() => {
    // when shipmentTypeId changes, set shippingCost to the shippingCost of the selected shipment type
    const company = deliveryCompanies.find((c) => c.id === form.getValues().delivery?.deliveryCompanyId)
    if (!company) return
    const shipmentType = company.shipmentTypes.find((type) => type.id === form.getValues().delivery?.shipmentTypeId)
    if (!shipmentType) return
    form.setFieldValue('delivery.shippingCost', shipmentType.shippingCost)
  }, [form.getValues().delivery?.shipmentTypeId])

  useEffect(() => {
    // when deliveryCompanyId changes, reset shipmentTypeId and shippingCost
    form.setFieldValue('delivery.shipmentTypeId', null!)
    form.setFieldValue('delivery.shippingCost', '' as unknown as number)
    form.setFieldValue('delivery.parcelLocker', '')
    form.setFieldValue('delivery.parcelSize', null)
  }, [form.getValues().delivery?.deliveryCompanyId])

  useEffect(() => {
    form.setFieldValue('delivery.parcelLocker', '')
    form.setFieldValue('delivery.parcelSize', null)
  }, [form.getValues().delivery?.shipmentTypeId])

  useEffect(() => {
    if (order) {
      form.setFieldValue('delivery.deliveryCompanyId', order.shipmentType.deliveryCompanyId)
      form.setFieldValue('delivery.shipmentTypeId', order.shipmentType.id)
      form.setFieldValue('delivery.parcelLocker', order.deliveryAddress.parcelLocker || null)
      form.setFieldValue('delivery.parcelSize', order.deliveryAddress.parcelSize || null)
      form.setFieldValue('delivery.shippingCost', order.shippingCost)
    }
  }, [order])

  const columns = useMemo<Array<MRT_ColumnDef<NewOrderItem>>>(
    () => [
      {
        accessorKey: 'productSymbol',
        header: 'Produkt',
        Cell: ({ cell }) => {
          const productSymbol = cell.getValue<string>()
          const category = getCategoryName(cell.row.original.category, { capitalize: true }).mianownik
          return (
            <Flex direction="column">
              <span>
                <b>{productSymbol}</b>
              </span>
              <span>{category}</span>
            </Flex>
          )
        },
        size: 150,
      },
      {
        accessorKey: 'size',
        header: 'Wymiar',
        size: 100,
        Cell: ({ cell }) => {
          const size = cell.getValue<string>()
          if (!size) return ''
          return `${size} cm`
        },
      },
      {
        accessorKey: 'price',
        header: 'Cena',
        size: 90,
        Cell: ({ cell }) => {
          const price = cell.getValue<number>()
          if (!price) return ''
          return `${price.toFixed(2)} zł`
        },
      },
      {
        accessorKey: 'amount',
        header: 'Ilość',
        Cell: ({ cell }) => {
          const amount = cell.getValue<string>()
          const unit = cell.row.original.unit
          if (!amount) return ''
          return `${amount} ${getUnitName(unit).short}`
        },
        size: 90,
      },
      {
        header: 'Wartość',
        Cell: ({ cell }) => {
          const price = cell.row.original.price
          const amount = cell.row.original.amount
          if (!price || !amount) return ''
          return `${(price * amount).toFixed(2)} zł`
        },
        size: 90,
      },
      {
        accessorKey: 'finish',
        header: 'Wykończenie',
        size: 130,
      },
      {
        accessorKey: 'comments',
        header: 'Uwagi',
        size: 130,
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data: orderItems,
    layoutMode: 'grid',
    editDisplayMode: 'table',
    enableSorting: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableBottomToolbar: 1 < 5 ? false : true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: !preview,
    positionActionsColumn: 'last',
    pageCount: 5,
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10'],
    },
    initialState: {
      density: 'xs',
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 60,
      },
    },
    renderRowActions: ({ row }) => (
      <Menu withArrow arrowSize={10} position="bottom-end" withinPortal offset={0} arrowOffset={8}>
        <Menu.Target>
          <ActionIcon radius="xl" variant="subtle" color="gray">
            <IconDots size={18} stroke={2} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconCopyPlus size={16} stroke={1.5} />}
            onClick={() => {
              const itemToDuplicate = row.original
              setOrderItems((current) => [...current, { ...itemToDuplicate, id: current.length.toString() }])
            }}
          >
            Duplikuj
          </Menu.Item>
          <Menu.Item
            leftSection={<IconEdit size={16} stroke={1.5} />}
            onClick={() => openUpdateOrderItemModal(row.original)}
          >
            Edytuj
          </Menu.Item>
          <Menu.Item
            leftSection={<IconTrash size={16} stroke={1.5} />}
            color="red"
            onClick={() => openDeleteOrderItemModal(row.original)}
          >
            Usuń
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ),
  })

  async function onSubmit(formData: NewOrder) {
    const payload = {
      ...formData,
      items: orderItems.map((item) => ({
        ...item,
        id: isGuid(item.id) ? item.id : '',
      })),
      totalPrice: parseFloat(totalOrderPrice),
    }
    if (order) {
      await updateOrder.mutateAsync({
        ...payload,
        id: order.id,
        dispatched,
      })
    } else {
      await addOrder.mutateAsync(payload)
    }
  }

  return (
    <Form
      onSubmit={onSubmit}
      form={form}
      onCancel={() => navigate({ to: '/orders' })}
      submitText={order ? 'Edytuj zamówienie' : 'Dodaj zamówienie'}
      disabledReason={orderItems.length === 0 ? 'Dodaj produkty do zamówienia' : null}
      withButtons={!preview}
    >
      <Stack gap="xs">
        <Fieldset legend="Dane zamawiającego">
          <Stack gap="xs">
            <Grid grow>
              <Grid.Col span={6}>
                <TextInput
                  label="Imię"
                  placeholder="Jan"
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('firstName')}
                  onChange={(value) => {
                    const formattedValue =
                      value.currentTarget.value.charAt(0).toUpperCase() +
                      value.currentTarget.value.slice(1).toLowerCase()
                    form.setFieldValue('firstName', formattedValue)
                  }}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Nazwisko"
                  placeholder="Kowalski"
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('lastName')}
                  onChange={(value) => {
                    const formattedValue =
                      value.currentTarget.value.charAt(0).toUpperCase() +
                      value.currentTarget.value.slice(1).toLowerCase()
                    form.setFieldValue('lastName', formattedValue)
                  }}
                />
              </Grid.Col>
            </Grid>
            <Grid grow>
              <Grid.Col span={6}>
                <TextInput
                  label="Email"
                  placeholder={preview ? '' : 'jan.kowalski@mail.com'}
                  description="Wymagany dla wszystkich kurierów oprócz Poczty Polskiej"
                  readOnly={preview}
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <PhoneNumberInput withAsterisk form={form} readOnly={preview} />
              </Grid.Col>
            </Grid>
            <Grid grow>
              <Grid.Col span={2}>
                <NumberInput
                  label="Rabat"
                  placeholder={preview ? '' : '5%'}
                  suffix="%"
                  allowNegative={false}
                  allowDecimal={false}
                  hideControls
                  min={0}
                  max={100}
                  clampBehavior="strict"
                  readOnly={preview}
                  {...form.getInputProps('discountPercent')}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label="Dokument sprzedaży"
                  data={documentTypes}
                  allowDeselect={false}
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('documentType')}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label="Rodzaj płatności"
                  data={paymentMethods}
                  allowDeselect={false}
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('paymentMethod')}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <NumberInput
                  label="NIP"
                  placeholder={preview ? '' : '1234563218'}
                  allowNegative={false}
                  allowDecimal={false}
                  hideControls
                  max={9999999999}
                  clampBehavior="strict"
                  readOnly={preview}
                  {...form.getInputProps('nip')}
                />
              </Grid.Col>
            </Grid>
            <Textarea
              label="Uwagi do zamówienia"
              description="Dodatkowe informacje które mają znaleźć się na zamówieniu, np. prośba o szybką realizację"
              placeholder={preview ? '' : 'Pilne zamówienie, proszę o szybką realizację.'}
              readOnly={preview}
              {...form.getInputProps('comments')}
            />
          </Stack>
        </Fieldset>
        <Fieldset legend="Adres dostawy">
          <Stack gap="xs">
            <Grid grow>
              <Grid.Col span={4}>
                <Select
                  label="Kurier"
                  description="Firma kurierska która dostarczy przesyłkę"
                  placeholder="Wybierz firmę kurierską z listy"
                  data={deliveryCompanies
                    .filter((company) => company.isActive && company.shipmentTypes.some((type) => type.isActive))
                    .map((company) => ({ label: company.name, value: company.id }))}
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('delivery.deliveryCompanyId')}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Select
                  label="Rodzaj przesyłki"
                  description="Rodzaj przesyłki oferowany przez firmę kurierską"
                  placeholder="Wybierz rodzaj przesyłki z listy"
                  disabled={!form.getValues().delivery?.deliveryCompanyId}
                  data={shipmentTypes}
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('delivery.shipmentTypeId')}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <NumberInput
                  label="Koszt przesyłki"
                  description="Użyj zapisanego kosztu przesyłki lub wpisz własny"
                  placeholder="15 zł"
                  suffix=" zł"
                  allowNegative={false}
                  allowDecimal={true}
                  decimalScale={2}
                  decimalSeparator=","
                  hideControls
                  withAsterisk
                  readOnly={preview}
                  {...form.getInputProps('delivery.shippingCost')}
                />
              </Grid.Col>
            </Grid>
            {isParcelLocker && (
              <Grid grow>
                <Grid.Col span={6}>
                  <TextInput
                    label="Numer paczkomatu"
                    description="Numer paczkomatu, jeśli wymagany"
                    placeholder="WAW100"
                    withAsterisk
                    readOnly={preview}
                    {...form.getInputProps('delivery.parcelLocker')}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Select
                    label="Rozmiar przesyłki"
                    description="Kategoria rozmiaru przesyłki do paczkomatu zgodna z wytycznymi operatora"
                    placeholder="Wybierz z listy"
                    withAsterisk
                    readOnly={preview}
                    data={[
                      { label: 'Mała (A) – maksymalne wymiary 8 × 38 × 64 cm', value: 'A' },
                      { label: 'Średnia (B) – maksymalne wymiary  19 × 38 × 64 cm', value: 'B' },
                      { label: 'Duża (C) – maksymalne wymiary  41 × 38 × 64 cm', value: 'C' },
                    ]}
                    {...form.getInputProps('delivery.parcelSize')}
                  />
                </Grid.Col>
              </Grid>
            )}
            {!isParcelLocker && (
              <TextInput
                label="Nazwa"
                placeholder={preview ? '' : 'Firma XYZ'}
                readOnly={preview}
                {...form.getInputProps('delivery.name')}
              />
            )}
            <Grid grow>
              <Grid.Col span={6}>
                <TextInput
                  label="Imię"
                  placeholder="Jan"
                  withAsterisk
                  disabled={isNameSameAsBilling}
                  readOnly={preview}
                  {...form.getInputProps('delivery.firstName')}
                  onChange={(value) => {
                    const formattedValue =
                      value.currentTarget.value.charAt(0).toUpperCase() +
                      value.currentTarget.value.slice(1).toLowerCase()
                    form.setFieldValue('delivery.firstName', formattedValue)
                  }}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Nazwisko"
                  placeholder="Kowalski"
                  withAsterisk
                  disabled={isNameSameAsBilling}
                  readOnly={preview}
                  {...form.getInputProps('delivery.lastName')}
                  onChange={(value) => {
                    const formattedValue =
                      value.currentTarget.value.charAt(0).toUpperCase() +
                      value.currentTarget.value.slice(1).toLowerCase()
                    form.setFieldValue('delivery.lastName', formattedValue)
                  }}
                />
              </Grid.Col>
            </Grid>
            {!preview && (
              <Checkbox
                label="Imię i nazwisko do dostawy jest takie same jak zamawiającego"
                checked={isNameSameAsBilling}
                onChange={({ currentTarget }) => setIsNameSameAsBilling(currentTarget.checked)}
              />
            )}
            {!isParcelLocker && (
              <>
                <Grid grow>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Ulica"
                      placeholder={preview ? '' : 'Szkolna'}
                      readOnly={preview}
                      withAsterisk
                      {...form.getInputProps('delivery.street')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Numer budynku"
                      placeholder="5"
                      withAsterisk
                      readOnly={preview}
                      {...form.getInputProps('delivery.buildingNumber')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Numer mieszkania"
                      placeholder={preview ? '' : '10'}
                      readOnly={preview}
                      {...form.getInputProps('delivery.apartmentNumber')}
                    />
                  </Grid.Col>
                </Grid>
                <Grid grow>
                  <Grid.Col span={4}>
                    <TextInput
                      label="Kod pocztowy"
                      placeholder="00-000"
                      withAsterisk
                      readOnly={preview}
                      {...form.getInputProps('delivery.postalCode')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label="Miejscowość"
                      placeholder="Warszawa"
                      withAsterisk
                      readOnly={preview}
                      {...form.getInputProps('delivery.city')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Autocomplete
                      label="Państwo"
                      placeholder="Polska"
                      data={countries}
                      withAsterisk
                      readOnly={preview}
                      filter={optionsFilter}
                      {...form.getInputProps('delivery.country')}
                    />
                  </Grid.Col>
                </Grid>
              </>
            )}
          </Stack>
        </Fieldset>
        {((order?.trackingNumber && dispatched) || !preview) && (
          <Fieldset legend="Informacje o przesyłce">
            <Stack gap="sm">
              {!preview && (
                <Switch
                  label="Przesyłka wysłana"
                  description="Ustawienie tutaj przesyłki jako wysłanej NIE spowoduje nadania przesyłki w systemie firmy kurierskiej - przesyłki trzeba nadać z widoku tabeli z zamówieniami. Tutaj można zaznaczyć przesyłkę jako wysłaną jeśli z jakiegoś powodu nie można jej nadać przez magazyn i trzeba to zrobić ręcznie przez system firmy kurierskiej. Jeśli ustawisz tutaj przesyłkę jako wysłaną, to nie będziesz mógł już jej nadać przez magazyn."
                  checked={dispatched}
                  onChange={(e) => setDispatched(e.currentTarget.checked)}
                />
              )}
              {order?.trackingNumber && dispatched && (
                <Grid grow>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Numer przesyłki"
                      description="Numer przesyłki nadany przez firmę kurierską do śledzenia przesyłki"
                      readOnly
                      rightSection={
                        <Tooltip label="Kopiuj numer przesyłki">
                          <ActionIcon
                            radius="sm"
                            size="md"
                            variant="light"
                            onClick={() => {
                              navigator.clipboard.writeText(order.trackingNumber)
                            }}
                          >
                            <IconCopy size={17} />
                          </ActionIcon>
                        </Tooltip>
                      }
                      value={order?.trackingNumber}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Link do śledzenia przesyłki"
                      description="Link który można udostępnić klientowi do śledzenia przesyłki"
                      readOnly
                      rightSection={
                        <Tooltip label="Kopiuj link do śledzenia przesyłki">
                          <ActionIcon
                            radius="sm"
                            size="md"
                            variant="light"
                            onClick={() => {
                              navigator.clipboard.writeText(getTrackingUrl(order) || '')
                            }}
                          >
                            <IconCopy size={17} />
                          </ActionIcon>
                        </Tooltip>
                      }
                      value={getTrackingUrl(order) || ''}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
          </Fieldset>
        )}
        <Fieldset
          legend={
            <Group gap="xs" mr="xs">
              <span>Produkty</span>
              {!preview && (
                <Tooltip label="Dodaj produkt do zamówienia">
                  <ActionIcon radius="xl" size="sm" variant="light" onClick={openAddOrderItemModal}>
                    <IconPlus size={17} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          }
        >
          <MantineReactTable table={table} />
        </Fieldset>
        <Fieldset legend="Podsumowanie zamówienia">
          <Table variant="vertical">
            <Table.Tbody>
              <Table.Tr>
                <Table.Th>Wartość produktów</Table.Th>
                <Table.Td>{totalItemsPrice} zł</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>W tym VAT (23%)</Table.Th>
                <Table.Td>{vatAmount} zł</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Koszt przesyłki</Table.Th>
                <Table.Td>{(form.getValues().delivery?.shippingCost || 0).toFixed(2)} zł</Table.Td>
              </Table.Tr>
              {!!discountPercent && (
                <Table.Tr>
                  <Table.Th>Rabat {discountPercent} %</Table.Th>
                  <Table.Td>- {discountValue} zł</Table.Td>
                </Table.Tr>
              )}
              <Table.Tr>
                <Table.Th>Do zapłaty</Table.Th>
                <Table.Td>{totalOrderPrice} zł</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Fieldset>
      </Stack>
    </Form>
  )
}
